import useFileSaver from '@hooks/useFileSaver';
import useRequest, { IDataRequest } from '@hooks/useRequest';
import { EnumEmpresa } from '@model/Empresa';
import { IRetorno } from '@model/Retorno';
import { IRetornoItem } from '@model/RetornoItem';
import { EnumRetornoItemStatus, IRetornoItemStatus } from '@model/RetornoItemStatus';
import React from 'react';

export interface IRetornoFilter {
    empresaId: EnumEmpresa,
    numero?: string
}

export interface IRetornoItemFilter {
    retornoItemStatus: EnumRetornoItemStatus
}

const controller = "/Retorno";

export function useRetornoService () {
    const _request = useRequest();
    const fileSaver = useFileSaver();

    const listar = React.useCallback((request: IDataRequest<IRetornoFilter>) => {
        return _request.getData<IRetorno[]>(`${controller}/Listar`, request);
    }, [_request]);

    const listarItens = React.useCallback((retornoId: number, filter: IRetornoItemFilter) => {
        return _request.get<IRetornoItem[]>(`${controller}/ListarItens/${retornoId}`, {
            params: filter
        })
    }, [_request]);

    const exportarItens = React.useCallback((retornoId: number, filter: IRetornoItemFilter) => {
        return _request.download(`${controller}/ExportarItens/${retornoId}`, {
            params: filter
        }).then(response => { 
            fileSaver.save(response, `retorno-itens-${retornoId}.csv`);
        });
    }, [_request, fileSaver]);

    const listarItemStatusAtivos = React.useCallback(() => {
        return _request.get<IRetornoItemStatus[]>(`${controller}/ListarItemStatusAtivos`);
    }, [_request]);

    const baixarArquivo = React.useCallback((retornoId: number) => {
        return _request.download(`${controller}/BaixarArquivo/${retornoId}`)
            .then(response => { 
                fileSaver.save(response, `retorno-${retornoId}.ret`);
            });
    }, [_request, fileSaver]);

    const importar = React.useCallback((arquivo: File) => {
        const formData = new FormData();
        formData.append('arquivo', arquivo);

        return _request.upload<IRetorno>(`${controller}/Importar`, formData);
    }, [_request]);

    return React.useMemo(() => ({
        listar, listarItens, listarItemStatusAtivos, baixarArquivo, importar, exportarItens
    }), [listar, listarItens, listarItemStatusAtivos, baixarArquivo, importar, exportarItens])
}